/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
/* tslint:disable:no-forward-ref */
var core_1 = require("@angular/core");
var forms_1 = require("@angular/forms");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var kendo_date_math_1 = require("@progress/kendo-date-math");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var kendo_licensing_1 = require("@progress/kendo-licensing");
var package_metadata_1 = require("../package-metadata");
var multiview_calendar_component_1 = require("./multiview-calendar.component");
var navigation_component_1 = require("./navigation.component");
var view_list_component_1 = require("./view-list.component");
var dom_service_1 = require("./services/dom.service");
var bus_view_service_1 = require("./services/bus-view.service");
var navigation_service_1 = require("./services/navigation.service");
var disabled_dates_service_1 = require("./services/disabled-dates.service");
var selection_service_1 = require("./services/selection.service");
var scroll_sync_service_1 = require("./services/scroll-sync.service");
var cell_template_directive_1 = require("./templates/cell-template.directive");
var month_cell_template_directive_1 = require("./templates/month-cell-template.directive");
var year_cell_template_directive_1 = require("./templates/year-cell-template.directive");
var decade_cell_template_directive_1 = require("./templates/decade-cell-template.directive");
var century_cell_template_directive_1 = require("./templates/century-cell-template.directive");
var weeknumber_cell_template_directive_1 = require("./templates/weeknumber-cell-template.directive");
var header_title_template_directive_1 = require("./templates/header-title-template.directive");
var navigation_item_template_directive_1 = require("./templates/navigation-item-template.directive");
var picker_service_1 = require("../common/picker.service");
var view_enum_1 = require("./models/view.enum");
var min_validator_1 = require("../validators/min.validator");
var max_validator_1 = require("../validators/max.validator");
var defaults_1 = require("../defaults");
var util_1 = require("../util");
var dom_queries_1 = require("../common/dom-queries");
var utils_1 = require("../common/utils");
var rxjs_1 = require("rxjs");
var BOTTOM_VIEW_DOC_LINK = 'http://www.telerik.com/kendo-angular-ui/components/dateinputs/api/CalendarComponent/#toc-bottomview';
var TOP_VIEW_DOC_LINK = 'http://www.telerik.com/kendo-angular-ui/components/dateinputs/api/CalendarComponent/#toc-topview';
var MIN_DOC_LINK = 'http://www.telerik.com/kendo-angular-ui/components/dateinputs/api/CalendarComponent/#toc-min';
var MAX_DOC_LINK = 'http://www.telerik.com/kendo-angular-ui/components/dateinputs/api/CalendarComponent/#toc-max';
var VALUE_DOC_LINK = 'http://www.telerik.com/kendo-angular-ui/components/dateinputs/calendar/#toc-using-with-json';
var virtualizationProp = function (x) { return x ? x.virtualization : null; };
var ɵ0 = virtualizationProp;
exports.ɵ0 = ɵ0;
/**
 * @hidden
 */
exports.CALENDAR_VALUE_ACCESSOR = {
    multi: true,
    provide: forms_1.NG_VALUE_ACCESSOR,
    useExisting: core_1.forwardRef(function () { return CalendarComponent; }) //tslint:disable-line:no-use-before-declare
};
/**
 * @hidden
 */
exports.CALENDAR_RANGE_VALIDATORS = {
    multi: true,
    provide: forms_1.NG_VALIDATORS,
    useExisting: core_1.forwardRef(function () { return CalendarComponent; }) //tslint:disable-line:no-use-before-declare
};
/**
 * @hidden
 */
exports.KENDO_INPUT_PROVIDER = {
    provide: kendo_angular_common_1.KendoInput,
    useExisting: core_1.forwardRef(function () { return CalendarComponent; }) //tslint:disable-line:no-use-before-declare
};
/**
 * Represents the [Kendo UI Calendar component for Angular]({% slug overview_calendar %}#toc-basic-usage).
 *
 * @example
 * ```ts
 * _@Component({
 * selector: 'my-app',
 * template: `
 *  <kendo-calendar></kendo-calendar>
 * `
 * })
 * export class AppComponent { }
 * ```
 */
var CalendarComponent = /** @class */ (function () {
    function CalendarComponent(bus, dom, element, navigator, renderer, cdr, ngZone, injector, scrollSyncService, disabledDatesService, localization, selectionService, pickerService) {
        this.bus = bus;
        this.dom = dom;
        this.element = element;
        this.navigator = navigator;
        this.renderer = renderer;
        this.cdr = cdr;
        this.ngZone = ngZone;
        this.injector = injector;
        this.scrollSyncService = scrollSyncService;
        this.disabledDatesService = disabledDatesService;
        this.localization = localization;
        this.selectionService = selectionService;
        this.pickerService = pickerService;
        /**
         * @hidden
         */
        this.id = kendo_angular_common_1.guid();
        /**
         * Determines whether the built-in min or max validators are enforced when validating a form.
         */
        this.rangeValidation = false;
        /**
         * Sets the Calendar selection mode
         * ([see example]({% slug multiple_selection_calendar %})).
         *
         * The available values are:
         * * `single` (default)
         * * `multiple`
         */
        this.selection = 'single';
        /**
         * Sets or gets the `disabled` property of the Calendar and
         * determines whether the component is active
         * ([see example]({% slug disabled_calendar %})).
         */
        this.disabled = false;
        /**
         * Sets or gets the `tabindex` property of the Calendar. Based on the
         * [HTML `tabindex`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex) behavior,
         * it determines whether the component is focusable.
         */
        this.tabindex = 0;
        /**
         * Sets or gets the `navigation` property of the Calendar
         * and determines whether the navigation side-bar will be displayed
         * ([see example]({% slug sidebar_calendar %})).
         * Applies to the [`infinite`]({% slug api_dateinputs_calendarcomponent %}#toc-type) Calendar only.
         */
        this.navigation = true;
        /**
         * Defines the active view that the Calendar initially renders
         * ([see example]({% slug activeview_calendar %})).
         * By default, the active view is `month`.
         *
         * > You have to set `activeView` within the `topView`-`bottomView` range.
         */
        this.activeView = view_enum_1.CalendarViewEnum[view_enum_1.CalendarViewEnum.month];
        /**
         * Defines the bottommost view to which the user can navigate
         * ([see example]({% slug dates_calendar %}#toc-partial-dates)).
         */
        this.bottomView = view_enum_1.CalendarViewEnum[view_enum_1.CalendarViewEnum.month];
        /**
         * Defines the topmost view to which the user can navigate
         * ([see example]({% slug sidebar_calendar %}#toc-partial-dates)).
         */
        this.topView = view_enum_1.CalendarViewEnum[view_enum_1.CalendarViewEnum.century];
        /**
         * Determines whether to enable animation when navigating to previous/next view.
         * Applies to the [`classic`]({% slug api_dateinputs_calendarcomponent %}#toc-type) Calendar only.
         *
         * > This feature uses the [Web Animations API](https://developer.mozilla.org/en-US/docs/Web/API/Web_Animations_API). In order to run the animation in browsers that do not support it, you need the `web-animations-js` polyfill.
         *
         * @default false
         */
        this.animateNavigation = false;
        /**
         * Determines whether to display a week number column in the `month` view
         * ([see example]({% slug weeknumcolumn_calendar %})).
         */
        this.weekNumber = false;
        /**
         * Fires when the active view is changed
         * ([more information and example]({% slug overview_calendar %}#toc-events)).
         */
        this.activeViewChange = new core_1.EventEmitter();
        /**
         * Fires when navigating in the currently active view
         * ([more information and example]({% slug events_calendar %})).
         */
        this.navigate = new core_1.EventEmitter();
        /**
         * Fires when the active view date is changed
         * ([more information and example]({% slug overview_calendar %}#toc-events)).
         * Applies to the [`infinite`]({% slug api_dateinputs_calendarcomponent %}#toc-type) Calendar only.
         */
        this.activeViewDateChange = new core_1.EventEmitter();
        /**
         * Fires when the value is changed
         * ([more information and example]({% slug overview_calendar %}#toc-events)).
         */
        this.valueChange = new core_1.EventEmitter();
        this.isActive = false;
        this.cellUID = kendo_angular_common_1.guid();
        this.selectedDates = [];
        this._min = new Date(defaults_1.MIN_DATE);
        this._max = new Date(defaults_1.MAX_DATE);
        this._focusedDate = util_1.getToday();
        this.onControlChange = util_1.noop;
        this.onControlTouched = util_1.noop;
        this.onValidatorChange = util_1.noop;
        this.minValidateFn = util_1.noop;
        this.maxValidateFn = util_1.noop;
        this.syncNavigation = true;
        this._type = 'infinite';
        this.domEvents = [];
        this.resolvedPromise = Promise.resolve(null);
        this.destroyed = false;
        kendo_licensing_1.validatePackage(package_metadata_1.packageMetadata);
        this.setClasses(element.nativeElement);
        if (this.pickerService) {
            this.pickerService.calendar = this;
        }
    }
    Object.defineProperty(CalendarComponent.prototype, "focusedDate", {
        get: function () {
            return this._focusedDate;
        },
        /**
         * Sets or gets the `focusedDate` property of the Calendar and
         * defines the focused date of the component
         * ([see example]({% slug dates_calendar %}#toc-focused-dates)).
         *
         * > If the Calendar is out of the min or max range, it normalizes the defined `focusedDate`.
         */
        set: function (focusedDate) {
            if (this.activeViewDate && !kendo_date_math_1.isEqual(this._focusedDate, focusedDate)) {
                var service = this.bus.service(this.activeViewEnum);
                var lastDayInPeriod = service.lastDayOfPeriod(this.activeViewDate);
                var isFocusedDateInRange = service.isInRange(focusedDate, this.activeViewDate, lastDayInPeriod);
                if (!isFocusedDateInRange) {
                    this.emitNavigate(focusedDate);
                }
            }
            this._focusedDate = focusedDate || util_1.getToday();
            this.setAriaActivedescendant();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "min", {
        get: function () {
            return this._min;
        },
        /**
         * Sets or gets the `min` property of the Calendar and
         * defines the minimum allowed date value
         * ([see example]({% slug dateranges_calendar %})).
         * By default, the `min` value is `1900-1-1`.
         */
        set: function (min) {
            this._min = min || new Date(defaults_1.MIN_DATE);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "max", {
        get: function () {
            return this._max;
        },
        /**
         * Sets or gets the `max` property of the Calendar and
         * defines the maximum allowed date value
         * ([see example]({% slug dateranges_calendar %})).
         * By default, the `max` value is `2099-12-31`.
         */
        set: function (max) {
            this._max = max || new Date(defaults_1.MAX_DATE);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "value", {
        /**
         * Sets or gets the `value` property of the Calendar and defines the selected value of the component.
         *
         * > The `value` has to be a valid
         * [JavaScript `Date`](https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Date)
         * instance when in `single` selection mode or an array of valid JavaScript Date instances when in `multiple` selection mode.
         */
        get: function () {
            return this._value;
        },
        set: function (candidate) {
            this.verifyValue(candidate);
            this._value = Array.isArray(candidate) ?
                candidate.filter(function (date) { return utils_1.isPresent(date); }).map(function (element) { return kendo_date_math_1.cloneDate(element); }) :
                kendo_date_math_1.cloneDate(candidate);
            var selection = [].concat(candidate).filter(function (date) { return utils_1.isPresent(date); }).map(function (date) { return kendo_date_math_1.cloneDate(date); });
            if (!util_1.areDatesEqual(selection, this.selectedDates)) {
                var lastSelected = util_1.last(selection);
                this.rangePivot = kendo_date_math_1.cloneDate(lastSelected);
                this.focusedDate = kendo_date_math_1.cloneDate(lastSelected) || this.focusedDate;
                this.selectedDates = selection;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "tabIndex", {
        get: function () {
            return this.tabindex;
        },
        /**
         * @hidden
         */
        set: function (tabIndex) {
            this.tabindex = tabIndex;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "disabledDates", {
        get: function () {
            return this._disabledDates;
        },
        /**
         * Sets the dates of the Calendar that will be disabled
         * ([see example]({% slug disabled_dates_calendar %})).
         */
        set: function (value) {
            this.disabledDatesService.initialize(value);
            this._disabledDates = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "type", {
        get: function () {
            return this._type;
        },
        /**
         * Specifies the Calendar type.
         *
         * The possible values are:
         * - `infinite` (default)
         * - `classic`
         *
         */
        set: function (type) {
            this.renderer.removeClass(this.element.nativeElement, "k-calendar-" + this.type);
            this.renderer.addClass(this.element.nativeElement, "k-calendar-" + type);
            this._type = type;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "cellTemplateRef", {
        get: function () {
            return this._cellTemplateRef || this.cellTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for each cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._cellTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "monthCellTemplateRef", {
        get: function () {
            return this._monthCellTemplateRef || this.monthCellTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for each month cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._monthCellTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "yearCellTemplateRef", {
        get: function () {
            return this._yearCellTemplateRef || this.yearCellTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for each year cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._yearCellTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "decadeCellTemplateRef", {
        get: function () {
            return this._decadeCellTemplateRef || this.decadeCellTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for each decade cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._decadeCellTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "centuryCellTemplateRef", {
        get: function () {
            return this._centuryCellTemplateRef || this.centuryCellTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for each century cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._centuryCellTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "weekNumberTemplateRef", {
        get: function () {
            return this._weekNumberTemplateRef || this.weekNumberTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for the week cell.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._weekNumberTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "headerTitleTemplateRef", {
        get: function () {
            return this._headerTitleTemplateRef || this.headerTitleTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for the header title.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._headerTitleTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "navigationItemTemplateRef", {
        get: function () {
            return this._navigationItemTemplateRef || this.navigationItemTemplate;
        },
        /**
         * @hidden
         *
         * Defines the template for the navigation item.
         * Takes precedence over nested templates in the KendoCalendar tag.
         */
        set: function (template) {
            this._navigationItemTemplateRef = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "activeViewEnum", {
        get: function () {
            var activeView = view_enum_1.CalendarViewEnum[this.activeView];
            return activeView < this.bottomViewEnum ? this.bottomViewEnum : activeView;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "bottomViewEnum", {
        get: function () {
            return view_enum_1.CalendarViewEnum[this.bottomView];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "topViewEnum", {
        get: function () {
            return view_enum_1.CalendarViewEnum[this.topView];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "widgetId", {
        get: function () {
            return this.id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "calendarTabIndex", {
        get: function () {
            // in Classic mode, the inner MultiViewCalendar is the focusable element
            return this.disabled || this.type === 'classic' ? undefined : this.tabIndex;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "ariaDisabled", {
        get: function () {
            // in Classic mode, the inner MultiViewCalendar should handle the disabled class and aria attr
            return this.type === 'classic' ? undefined : this.disabled;
        },
        enumerable: true,
        configurable: true
    });
    CalendarComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.dom.calculateHeights(this.element.nativeElement);
        this.scrollSyncService.configure(this.activeViewEnum);
        this.localizationChangeSubscription = this.localization.changes.subscribe(function () { return _this.cdr.markForCheck(); });
        this.viewChangeSubscription = this.bus.viewChanged.subscribe(function (_a) {
            var view = _a.view;
            return _this.handleActiveViewChange(view_enum_1.CalendarViewEnum[view]);
        });
        this.control = this.injector.get(forms_1.NgControl, null);
        if (this.element) {
            this.ngZone.runOutsideAngular(function () {
                _this.bindEvents();
            });
        }
    };
    CalendarComponent.prototype.ngOnChanges = function (changes) {
        this.verifyChanges();
        this.bus.configure(this.bottomViewEnum, this.topViewEnum);
        this.scrollSyncService.configure(this.activeViewEnum);
        if (util_1.hasExistingValue(changes, 'focusedDate')) {
            var focusedDate = changes.focusedDate.currentValue;
            this.focusedDate = util_1.dateInRange(focusedDate, this.min, this.max);
        }
        if (changes.navigation) {
            this.syncNavigation = true;
        }
        if (changes.min || changes.max || changes.rangeValidation) {
            this.minValidateFn = this.rangeValidation ? min_validator_1.minValidator(this.min) : util_1.noop;
            this.maxValidateFn = this.rangeValidation ? max_validator_1.maxValidator(this.max) : util_1.noop;
            this.onValidatorChange();
        }
    };
    CalendarComponent.prototype.ngAfterViewInit = function () {
        this.setAriaActivedescendant();
    };
    CalendarComponent.prototype.ngAfterViewChecked = function () {
        if (!this.syncNavigation) {
            return;
        }
        this.syncNavigation = false;
        this.scrollSyncService.sync(virtualizationProp(this.navigationView), virtualizationProp(this.monthView));
    };
    CalendarComponent.prototype.ngOnDestroy = function () {
        this.scrollSyncService.destroy();
        this.domEvents.forEach(function (unbindCallback) { return unbindCallback(); });
        if (this.pickerService) {
            this.pickerService.calendar = null;
        }
        if (this.viewChangeSubscription) {
            this.viewChangeSubscription.unsubscribe();
        }
        if (this.pageChangeSubscription) {
            this.pageChangeSubscription.unsubscribe();
        }
        if (this.localizationChangeSubscription) {
            this.localizationChangeSubscription.unsubscribe();
        }
        this.destroyed = true;
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.onResize = function () {
        this.focusedDate = new Date(this.focusedDate);
        this.cdr.detectChanges();
    };
    /**
     * Focuses the host element of the Calendar.
     *
     * @example
     * ```ts
     * _@Component({
     * selector: 'my-app',
     * template: `
     *  <button (click)="calendar.focus()">Focus calendar</button>
     *  <kendo-calendar #calendar></kendo-calendar>
     * `
     * })
     * export class AppComponent { }
     * ```
     */
    CalendarComponent.prototype.focus = function () {
        var focusTarget = this.type === 'infinite' ?
            this.element && this.element.nativeElement :
            this.multiViewCalendar;
        if (utils_1.isPresent(focusTarget)) {
            focusTarget.focus();
        }
    };
    /**
     * Blurs the Calendar component.
     */
    CalendarComponent.prototype.blur = function () {
        var blurTarget = this.type === 'infinite' ?
            this.element && this.element.nativeElement :
            this.multiViewCalendar;
        if (utils_1.isPresent(blurTarget)) {
            blurTarget.blur();
        }
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.containsElement = function (element) {
        var _this = this;
        return Boolean(dom_queries_1.closest(element, function (node) { return node === _this.element.nativeElement; }));
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleNavigation = function (candidate) {
        if (this.disabled) {
            return;
        }
        var focusTarget = candidate ? new Date(kendo_date_math_1.cloneDate(candidate).setDate(1)) : this.focusedDate;
        this.focusedDate = util_1.dateInRange(focusTarget, this.min, this.max);
        this.detectChanges();
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.onPageChange = function () {
        var _this = this;
        if (!core_1.NgZone.isInAngularZone()) {
            if (this.pageChangeSubscription) {
                this.pageChangeSubscription.unsubscribe();
            }
            this.pageChangeSubscription = rxjs_1.from(this.resolvedPromise)
                .subscribe(function () {
                _this.detectChanges(); // requires zone if templates
            });
        }
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleMultiViewCalendarValueChange = function (date, focusedDate) {
        var selectedDates = Array.isArray(date) ? date : [date];
        this.handleDateChange({ selectedDates: selectedDates, focusedDate: focusedDate });
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleDateChange = function (args) {
        var _this = this;
        var selectedDates = Array.isArray(args.selectedDates) ? args.selectedDates : [args.selectedDates];
        var canNavigateDown = this.bus.canMoveDown(this.activeViewEnum);
        var availableDates = selectedDates.filter(function (date) { return !_this.disabledDatesService.isDateDisabled(date); });
        this.focusedDate = args.focusedDate || this.focusedDate;
        if (this.disabled) {
            return;
        }
        if (!canNavigateDown && util_1.areDatesEqual(availableDates, this.selectedDates)) {
            this.emitSameDate();
            return;
        }
        if (canNavigateDown) {
            this.bus.moveDown(this.activeViewEnum);
            return;
        }
        if (this.disabledDatesService.isDateDisabled(this.focusedDate)) {
            return;
        }
        this.ngZone.run(function () {
            _this.selectedDates = availableDates.map(function (date) { return kendo_date_math_1.cloneDate(date); });
            _this.value = _this.parseSelectionToValue(availableDates);
            _this.onControlChange(_this.parseSelectionToValue(availableDates));
            _this.valueChange.emit(_this.parseSelectionToValue(availableDates));
            _this.cdr.markForCheck();
        });
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.writeValue = function (candidate) {
        this.verifyValue(candidate);
        this.value = candidate;
        this.cdr.markForCheck();
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.registerOnChange = function (fn) {
        this.onControlChange = fn;
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.registerOnTouched = function (fn) {
        this.onControlTouched = fn;
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.setDisabledState = function (isDisabled) {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.validate = function (control) {
        return this.minValidateFn(control) || this.maxValidateFn(control);
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.registerOnValidatorChange = function (fn) {
        this.onValidatorChange = fn;
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.activeCellTemplate = function () {
        switch (this.activeViewEnum) {
            case view_enum_1.CalendarViewEnum.month:
                return this.monthCellTemplateRef || this.cellTemplateRef;
            case view_enum_1.CalendarViewEnum.year:
                return this.yearCellTemplateRef;
            case view_enum_1.CalendarViewEnum.decade:
                return this.decadeCellTemplateRef;
            case view_enum_1.CalendarViewEnum.century:
                return this.centuryCellTemplateRef;
            default:
                return null;
        }
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleNavigate = function (event) {
        this.focusedDate = event.focusedDate;
        this.activeView = event.activeView;
        this.emitNavigate(this.focusedDate);
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.emitNavigate = function (focusedDate) {
        var activeView = view_enum_1.CalendarViewEnum[this.activeViewEnum];
        this.navigate.emit({ activeView: activeView, focusedDate: focusedDate });
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.emitEvent = function (emitter, args) {
        if (kendo_angular_common_1.hasObservers(emitter)) {
            this.ngZone.run(function () {
                emitter.emit(args);
            });
        }
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleActiveDateChange = function (date) {
        this.activeViewDate = date;
        this.emitEvent(this.activeViewDateChange, date);
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleActiveViewChange = function (view) {
        this.activeView = view;
        this.emitEvent(this.activeViewChange, view);
        if (this.type === 'infinite') {
            this.scrollSyncService.configure(this.activeViewEnum);
        }
        this.detectChanges(); // requires zone if templates
    };
    CalendarComponent.prototype.handleCellClick = function (_a) {
        var date = _a.date, modifiers = _a.modifiers;
        this.performSelection(date, modifiers);
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleWeekNumberClick = function (dates) {
        var _this = this;
        if (this.selection === 'single') {
            return;
        }
        this.ngZone.run(function () {
            _this.handleDateChange({
                selectedDates: dates,
                focusedDate: util_1.last(dates)
            });
        });
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleBlur = function (args) {
        var _this = this;
        if (this.element.nativeElement.contains(args.relatedTarget)) {
            return;
        }
        this.isActive = false;
        // the injector can get the NgControl instance of the parent component (for example, the DateTimePicker)
        // and enters the zone for no reason because the parent component is still untouched
        if (!this.pickerService && utils_1.requiresZoneOnBlur(this.control)) {
            this.ngZone.run(function () {
                _this.onControlTouched();
                _this.emitBlur(args);
                _this.cdr.markForCheck();
            });
        }
        else {
            this.emitBlur(args);
            this.detectChanges();
        }
    };
    /**
     * @hidden
     */
    CalendarComponent.prototype.handleFocus = function () {
        this.isActive = true;
        if (!core_1.NgZone.isInAngularZone()) {
            this.detectChanges();
        }
        this.emitFocus();
    };
    CalendarComponent.prototype.setClasses = function (element) {
        this.renderer.addClass(element, 'k-widget');
        this.renderer.addClass(element, 'k-calendar');
        this.renderer.addClass(element, "k-calendar-" + this.type);
    };
    CalendarComponent.prototype.verifyChanges = function () {
        if (!core_1.isDevMode()) {
            return;
        }
        if (this.min > this.max) {
            throw new Error("The max value should be bigger than the min. See " + MIN_DOC_LINK + " and " + MAX_DOC_LINK + ".");
        }
        if (this.bottomViewEnum > this.topViewEnum) {
            throw new Error("The topView should be greater than bottomView. See " + BOTTOM_VIEW_DOC_LINK + " and " + TOP_VIEW_DOC_LINK + ".");
        }
    };
    CalendarComponent.prototype.verifyValue = function (candidate) {
        if (!core_1.isDevMode()) {
            return;
        }
        if (this.selection === 'single' && candidate && !(candidate instanceof Date)) {
            throw new Error("The 'value' should be a valid JavaScript Date instance. Check " + VALUE_DOC_LINK + " for possible resolution.");
        }
        else if (this.selection === 'multiple' && candidate && Array.isArray(candidate)) {
            var onlyDates = candidate.every(function (value) { return value instanceof Date; });
            if (!onlyDates) {
                throw new Error("The 'value' should be an array of valid JavaScript Date instances. Check " + VALUE_DOC_LINK + " for possible resolution.");
            }
        }
    };
    CalendarComponent.prototype.bindEvents = function () {
        var element = this.element.nativeElement;
        this.domEvents.push(this.renderer.listen(element, 'blur', this.handleBlur.bind(this)), this.renderer.listen(element, 'focus', this.handleFocus.bind(this)), this.renderer.listen(element, 'mousedown', utils_1.preventDefault), this.renderer.listen(element, 'click', this.handleComponentClick.bind(this)), this.renderer.listen(element, 'keydown', this.handleKeydown.bind(this)));
    };
    CalendarComponent.prototype.emitBlur = function (args) {
        if (this.pickerService) {
            this.pickerService.onBlur.emit(args);
        }
    };
    CalendarComponent.prototype.emitFocus = function () {
        if (this.pickerService) {
            this.pickerService.onFocus.emit();
        }
    };
    CalendarComponent.prototype.handleComponentClick = function () {
        if (!this.isActive) {
            if (this.type === 'infinite' && this.monthView.isScrolled()) {
                this.focusedDate = kendo_date_math_1.cloneDate(this.focusedDate); //XXX: forces change detect
                this.detectChanges();
            }
            this.focus();
        }
    };
    CalendarComponent.prototype.handleKeydown = function (args) {
        var headerActive = this.type === 'classic' && this.multiViewCalendar.isHeaderActive;
        if (headerActive) {
            return;
        }
        // reserve the alt + arrow key commands for the picker
        var arrowKeyPressed = [kendo_angular_common_1.Keys.ArrowUp, kendo_angular_common_1.Keys.ArrowRight, kendo_angular_common_1.Keys.ArrowDown, kendo_angular_common_1.Keys.ArrowLeft].indexOf(args.keyCode) !== -1;
        if (utils_1.isPresent(this.pickerService) && arrowKeyPressed && args.altKey) {
            return;
        }
        var candidate = util_1.dateInRange(this.navigator.move(this.focusedDate, this.navigator.action(args), this.activeViewEnum), this.min, this.max);
        if (!kendo_date_math_1.isEqual(this.focusedDate, candidate)) {
            this.focusedDate = candidate;
            this.detectChanges();
            args.preventDefault();
        }
        if (args.keyCode === kendo_angular_common_1.Keys.Enter) {
            this.performSelection(this.focusedDate, args);
        }
    };
    CalendarComponent.prototype.detectChanges = function () {
        if (!this.destroyed) {
            this.cdr.detectChanges();
        }
    };
    CalendarComponent.prototype.emitSameDate = function () {
        if (this.pickerService) {
            this.pickerService.sameDateSelected.emit();
        }
    };
    CalendarComponent.prototype.setAriaActivedescendant = function () {
        // in Classic mode, the inner MultiViewCalendar handles the activedescendant
        if (!utils_1.isPresent(this.element) || (this.type === 'classic' && !this.element.nativeElement.hasAttribute('aria-activedescendant'))) {
            return;
        }
        if (this.type === 'classic') {
            this.renderer.removeAttribute(this.element.nativeElement, 'aria-activedescendant');
            return;
        }
        var focusedCellId = this.cellUID + this.focusedDate.getTime();
        this.renderer.setAttribute(this.element.nativeElement, 'aria-activedescendant', focusedCellId);
    };
    CalendarComponent.prototype.parseSelectionToValue = function (selection) {
        selection = selection || [];
        return this.selection === 'single' ? kendo_date_math_1.cloneDate(util_1.last(selection)) : selection.map(function (date) { return kendo_date_math_1.cloneDate(date); });
    };
    CalendarComponent.prototype.performSelection = function (date, selectionModifiers) {
        var selection = this.selectionService.performSelection({
            date: date,
            modifiers: selectionModifiers,
            selectionMode: this.selection,
            activeViewEnum: this.activeViewEnum,
            rangePivot: this.rangePivot,
            selectedDates: this.selectedDates
        });
        this.rangePivot = selection.rangePivot;
        this.handleDateChange({
            selectedDates: selection.selectedDates,
            focusedDate: date
        });
    };
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], CalendarComponent.prototype, "id", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Date),
        tslib_1.__metadata("design:paramtypes", [Date])
    ], CalendarComponent.prototype, "focusedDate", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Date),
        tslib_1.__metadata("design:paramtypes", [Date])
    ], CalendarComponent.prototype, "min", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Date),
        tslib_1.__metadata("design:paramtypes", [Date])
    ], CalendarComponent.prototype, "max", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], CalendarComponent.prototype, "rangeValidation", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], CalendarComponent.prototype, "selection", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], CalendarComponent.prototype, "value", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], CalendarComponent.prototype, "disabled", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Number)
    ], CalendarComponent.prototype, "tabindex", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Number),
        tslib_1.__metadata("design:paramtypes", [Number])
    ], CalendarComponent.prototype, "tabIndex", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], CalendarComponent.prototype, "disabledDates", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], CalendarComponent.prototype, "navigation", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], CalendarComponent.prototype, "activeView", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], CalendarComponent.prototype, "bottomView", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], CalendarComponent.prototype, "topView", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String),
        tslib_1.__metadata("design:paramtypes", [String])
    ], CalendarComponent.prototype, "type", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], CalendarComponent.prototype, "animateNavigation", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        core_1.HostBinding('class.k-week-number'),
        tslib_1.__metadata("design:type", Boolean)
    ], CalendarComponent.prototype, "weekNumber", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], CalendarComponent.prototype, "activeViewChange", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], CalendarComponent.prototype, "navigate", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], CalendarComponent.prototype, "activeViewDateChange", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], CalendarComponent.prototype, "valueChange", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(cell_template_directive_1.CellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", cell_template_directive_1.CellTemplateDirective)
    ], CalendarComponent.prototype, "cellTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('cellTemplate'),
        tslib_1.__metadata("design:type", cell_template_directive_1.CellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [cell_template_directive_1.CellTemplateDirective])
    ], CalendarComponent.prototype, "cellTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(month_cell_template_directive_1.MonthCellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", month_cell_template_directive_1.MonthCellTemplateDirective)
    ], CalendarComponent.prototype, "monthCellTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('monthCellTemplate'),
        tslib_1.__metadata("design:type", month_cell_template_directive_1.MonthCellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [month_cell_template_directive_1.MonthCellTemplateDirective])
    ], CalendarComponent.prototype, "monthCellTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(year_cell_template_directive_1.YearCellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", year_cell_template_directive_1.YearCellTemplateDirective)
    ], CalendarComponent.prototype, "yearCellTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('yearCellTemplate'),
        tslib_1.__metadata("design:type", year_cell_template_directive_1.YearCellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [year_cell_template_directive_1.YearCellTemplateDirective])
    ], CalendarComponent.prototype, "yearCellTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(decade_cell_template_directive_1.DecadeCellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", decade_cell_template_directive_1.DecadeCellTemplateDirective)
    ], CalendarComponent.prototype, "decadeCellTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('decadeCellTemplate'),
        tslib_1.__metadata("design:type", decade_cell_template_directive_1.DecadeCellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [decade_cell_template_directive_1.DecadeCellTemplateDirective])
    ], CalendarComponent.prototype, "decadeCellTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(century_cell_template_directive_1.CenturyCellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", century_cell_template_directive_1.CenturyCellTemplateDirective)
    ], CalendarComponent.prototype, "centuryCellTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('centuryCellTemplate'),
        tslib_1.__metadata("design:type", century_cell_template_directive_1.CenturyCellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [century_cell_template_directive_1.CenturyCellTemplateDirective])
    ], CalendarComponent.prototype, "centuryCellTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(weeknumber_cell_template_directive_1.WeekNumberCellTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", weeknumber_cell_template_directive_1.WeekNumberCellTemplateDirective)
    ], CalendarComponent.prototype, "weekNumberTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('weekNumberTemplate'),
        tslib_1.__metadata("design:type", weeknumber_cell_template_directive_1.WeekNumberCellTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [weeknumber_cell_template_directive_1.WeekNumberCellTemplateDirective])
    ], CalendarComponent.prototype, "weekNumberTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(header_title_template_directive_1.HeaderTitleTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", header_title_template_directive_1.HeaderTitleTemplateDirective)
    ], CalendarComponent.prototype, "headerTitleTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('headerTitleTemplate'),
        tslib_1.__metadata("design:type", header_title_template_directive_1.HeaderTitleTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [header_title_template_directive_1.HeaderTitleTemplateDirective])
    ], CalendarComponent.prototype, "headerTitleTemplateRef", null);
    tslib_1.__decorate([
        core_1.ContentChild(navigation_item_template_directive_1.NavigationItemTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", navigation_item_template_directive_1.NavigationItemTemplateDirective)
    ], CalendarComponent.prototype, "navigationItemTemplate", void 0);
    tslib_1.__decorate([
        core_1.Input('navigationItemTemplate'),
        tslib_1.__metadata("design:type", navigation_item_template_directive_1.NavigationItemTemplateDirective),
        tslib_1.__metadata("design:paramtypes", [navigation_item_template_directive_1.NavigationItemTemplateDirective])
    ], CalendarComponent.prototype, "navigationItemTemplateRef", null);
    tslib_1.__decorate([
        core_1.ViewChild(navigation_component_1.NavigationComponent, { static: false }),
        tslib_1.__metadata("design:type", navigation_component_1.NavigationComponent)
    ], CalendarComponent.prototype, "navigationView", void 0);
    tslib_1.__decorate([
        core_1.ViewChild(view_list_component_1.ViewListComponent, { static: false }),
        tslib_1.__metadata("design:type", view_list_component_1.ViewListComponent)
    ], CalendarComponent.prototype, "monthView", void 0);
    tslib_1.__decorate([
        core_1.ViewChild(multiview_calendar_component_1.MultiViewCalendarComponent, { static: false }),
        tslib_1.__metadata("design:type", multiview_calendar_component_1.MultiViewCalendarComponent)
    ], CalendarComponent.prototype, "multiViewCalendar", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('attr.id'),
        tslib_1.__metadata("design:type", String),
        tslib_1.__metadata("design:paramtypes", [])
    ], CalendarComponent.prototype, "widgetId", null);
    tslib_1.__decorate([
        core_1.HostBinding('attr.tabindex'),
        tslib_1.__metadata("design:type", Number),
        tslib_1.__metadata("design:paramtypes", [])
    ], CalendarComponent.prototype, "calendarTabIndex", null);
    tslib_1.__decorate([
        core_1.HostBinding('attr.aria-disabled'),
        core_1.HostBinding('class.k-disabled'),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [])
    ], CalendarComponent.prototype, "ariaDisabled", null);
    CalendarComponent = tslib_1.__decorate([
        core_1.Component({
            changeDetection: core_1.ChangeDetectionStrategy.OnPush,
            exportAs: 'kendo-calendar',
            providers: [
                bus_view_service_1.BusViewService,
                exports.CALENDAR_VALUE_ACCESSOR,
                exports.CALENDAR_RANGE_VALIDATORS,
                exports.KENDO_INPUT_PROVIDER,
                kendo_angular_l10n_1.LocalizationService,
                disabled_dates_service_1.DisabledDatesService,
                {
                    provide: kendo_angular_l10n_1.L10N_PREFIX,
                    useValue: 'kendo.calendar'
                },
                navigation_service_1.NavigationService,
                scroll_sync_service_1.ScrollSyncService,
                selection_service_1.SelectionService
            ],
            selector: 'kendo-calendar',
            template: "\n    <ng-container kendoCalendarLocalizedMessages\n        i18n-today=\"kendo.calendar.today|The label for the today button in the calendar header\"\n        today=\"Today\"\n\n        i18n-prevButtonTitle=\"kendo.calendar.prevButtonTitle|The title of the previous button in the Classic calendar\"\n        prevButtonTitle=\"Navigate to previous view\"\n\n        i18n-nextButtonTitle=\"kendo.calendar.nextButtonTitle|The title of the next button in the Classic calendar\"\n        nextButtonTitle=\"Navigate to next view\"\n    >\n    </ng-container>\n    <ng-container *ngIf=\"type === 'infinite'\">\n        <kendo-calendar-navigation\n            *ngIf=\"navigation\"\n            [activeView]=\"activeViewEnum\"\n            [focusedDate]=\"focusedDate\"\n            [min]=\"min\"\n            [max]=\"max\"\n            [templateRef]=\"navigationItemTemplateRef?.templateRef\"\n            (valueChange)=\"handleNavigation($event)\"\n            (pageChange)=\"onPageChange()\"\n        >\n        </kendo-calendar-navigation>\n        <kendo-calendar-viewlist\n            [activeView]=\"activeViewEnum\"\n            [isActive]=\"isActive\"\n            [cellTemplateRef]=\"activeCellTemplate()?.templateRef\"\n            [headerTitleTemplateRef]=\"headerTitleTemplateRef?.templateRef\"\n            [weekNumberTemplateRef]=\"weekNumberTemplateRef?.templateRef\"\n            [cellUID]=\"cellUID\"\n            [min]=\"min\"\n            [max]=\"max\"\n            [focusedDate]=\"focusedDate\"\n            [weekNumber]=\"weekNumber\"\n            [selectedDates]=\"selectedDates\"\n            (todayButtonClick)=\"handleDateChange({\n                selectedDates: [$event],\n                focusedDate: $event\n            })\"\n            (cellClick)=\"handleCellClick($event)\"\n            (weekNumberCellClick)=\"handleWeekNumberClick($event)\"\n            (activeDateChange)=\"handleActiveDateChange($event)\"\n            (pageChange)=\"onPageChange()\"\n        >\n        </kendo-calendar-viewlist>\n        <kendo-resize-sensor (resize)=\"onResize()\"></kendo-resize-sensor>\n    </ng-container>\n    <ng-container *ngIf=\"type === 'classic'\">\n        <kendo-multiviewcalendar\n            #multiviewcalendar\n            [views]=\"1\"\n            [min]=\"min\"\n            [max]=\"max\"\n            [isActive]=\"isActive\"\n            [activeView]=\"activeView\"\n            [bottomView]=\"bottomView\"\n            [topView]=\"topView\"\n            [weekNumber]=\"weekNumber\"\n            [animateNavigation]=\"animateNavigation\"\n            [cellTemplate]=\"activeCellTemplate()\"\n            [monthCellTemplate]=\"monthCellTemplateRef\"\n            [yearCellTemplate]=\"yearCellTemplateRef\"\n            [decadeCellTemplate]=\"decadeCellTemplateRef\"\n            [centuryCellTemplate]=\"centuryCellTemplateRef\"\n            [headerTitleTemplate]=\"headerTitleTemplateRef\"\n            [weekNumberTemplate]=\"weekNumberTemplateRef\"\n            [focusedDate]=\"focusedDate\"\n            [selection]=\"selection\"\n            [value]=\"value\"\n            [disabledDates]=\"disabledDates\"\n            (activeViewChange)=\"handleActiveViewChange($event)\"\n            (navigate)=\"handleNavigate($event)\"\n            (valueChange)=\"handleMultiViewCalendarValueChange($event, multiviewcalendar.focusedDate)\"\n            (focus)=\"handleFocus()\"\n            (blur)=\"handleBlur($event)\"\n        >\n            <kendo-multiviewcalendar-messages\n                [today]=\"localization.get('today')\"\n                [prevButtonTitle]=\"localization.get('prevButtonTitle')\"\n                [nextButtonTitle]=\"localization.get('nextButtonTitle')\"\n            >\n            </kendo-multiviewcalendar-messages>\n        </kendo-multiviewcalendar>\n    </ng-container>\n  "
        }),
        tslib_1.__param(12, core_1.Optional()),
        tslib_1.__metadata("design:paramtypes", [bus_view_service_1.BusViewService,
            dom_service_1.CalendarDOMService,
            core_1.ElementRef,
            navigation_service_1.NavigationService,
            core_1.Renderer2,
            core_1.ChangeDetectorRef,
            core_1.NgZone,
            core_1.Injector,
            scroll_sync_service_1.ScrollSyncService,
            disabled_dates_service_1.DisabledDatesService,
            kendo_angular_l10n_1.LocalizationService,
            selection_service_1.SelectionService,
            picker_service_1.PickerService])
    ], CalendarComponent);
    return CalendarComponent;
}());
exports.CalendarComponent = CalendarComponent;
